// /src/components/imovel/ImovelCard.jsx
// VERSÃO 11.2 ELITE - TIM UNITY COMMAND CARD (RESOLVEDOR DE MÍDIA DINÂMICO)

import React from 'react';
import { 
    FiGlobe, FiPhone, FiEdit, FiCheckCircle, FiShield, 
    FiClock, FiDollarSign, FiChevronRight, FiEye, FiTrash2, FiZap 
} from 'react-icons/fi';
import { motion } from 'framer-motion';

// 📡 RESOLVEDOR DE IMAGENS INTELIGENTE
export const resolveImagePath = (path) => {
    if (!path) return 'https://via.placeholder.com/600x400?text=TIM+UNITY+OFERTA';
    if (path.startsWith('http')) return path;
    
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Tenta pegar a base da API (removendo /api) ou deixa relativo
    const baseUrl = import.meta.env.VITE_API_BASE_URL 
        ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') 
        : ''; 
        
    return `${baseUrl}${formattedPath}`;
};

export default function ImovelCard({ imovel, onAction, userRole, isPublic = false }) {
    const isMaster = userRole === 'MASTER' || userRole === 'ADM';
    const isOperator = userRole === 'USER' || isMaster;
    
    const tipoOferta = imovel.tipoNegocio || 'Plano';
    const valorPlano = imovel.valores?.venda || imovel.valorVenda || 0;
    
    const ligacoes = imovel.caracteristicas?.quartos || imovel.quartos || 'Não';
    const fidelidade = imovel.valores?.iptu || imovel.valorIptu || '24 Meses';
    const cobertura = imovel.nivelCobertura || 'Brasil';

    const fmt = (val) => {
        if (!val || val === 0) return 'Sob Consulta';
        return new Intl.NumberFormat('pt-BR', { 
            style: 'currency', currency: 'BRL', maximumFractionDigits: 2 
        }).format(val);
    };

    // Usando o resolvedor tático
    const imageUrl = resolveImagePath(imovel?.imagensDestaque?.[0] || imovel?.imagens?.[0]);

    return (
        <div className="group relative border rounded-[2.5rem] overflow-hidden transition-all duration-700 hover:translate-y-[-10px] shadow-2xl"
             style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>

            {/* HUD DE COMANDO */}
            {!isPublic && (
                <div className="absolute top-5 right-5 z-40 flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-4 group-hover:translate-x-0">
                    <CommandBtn icon={FiEye} label="Inspecionar" onClick={() => onAction(imovel._id, 'previa')} />
                    {isOperator && <CommandBtn icon={FiEdit} color="text-accent" label="Editar" onClick={() => onAction(imovel._id, 'edit')} />}
                    {isMaster && imovel.status !== 'Aprovado' && <CommandBtn icon={FiCheckCircle} color="text-green-500" label="Validar" onClick={() => onAction(imovel._id, 'aprovar')} />}
                    {isMaster && <CommandBtn icon={FiTrash2} color="text-red-500" label="Excluir" onClick={() => onAction(imovel._id, 'incinerar')} />}
                </div>
            )}

            {/* ZONA VISUAL */}
            <div className="relative h-64 overflow-hidden bg-black">
                <img src={imageUrl} className="w-full h-full object-cover transition-transform duration-[3s] group-hover:scale-110 opacity-80" alt={imovel.titulo} />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] via-transparent to-transparent opacity-90" />
                
                <div className="absolute top-5 left-5 flex flex-col gap-2">
                    <span className="backdrop-blur-xl bg-black/60 border border-white/10 px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-[0.2em] text-white self-start">
                        {tipoOferta}
                    </span>
                </div>

                <div className="absolute bottom-5 left-6 text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: 'var(--accent-color)' }}>
                    REF: {imovel.codigoImovel || 'TIM-SKU'}
                </div>
            </div>

            {/* DADOS OPERACIONAIS */}
            <div className="p-8 space-y-6">
                <div className="space-y-1">
                    <h3 className="text-xl font-black uppercase truncate group-hover:text-accent transition-colors text-auto">
                        {imovel.titulo}
                    </h3>
                    <div className="flex items-center gap-2 opacity-40 uppercase tracking-[0.2em] text-[9px] text-auto">
                        <FiGlobe size={10} className="text-accent" />
                        COBERTURA: {cobertura}
                    </div>
                </div>

                <div className="pt-2">
                    <p className="text-3xl font-black tracking-tighter leading-none text-auto flex items-baseline">
                        {fmt(valorPlano)}
                        <span className="text-xs opacity-30 ml-1 font-bold">/MÊS</span>
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-6 border-t border-[var(--border-color)]">
                    <SpecMini icon={FiPhone} value={ligacoes === 'Sim' ? 'Ilimitadas' : 'Limitadas'} />
                    <SpecMini icon={FiClock} value={fidelidade} />
                </div>
            </div>

            <div className="h-[4px] w-full bg-transparent relative">
                <div className="absolute inset-0 scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"
                     style={{ backgroundColor: 'var(--accent-color)', boxShadow: `0 0 15px var(--accent-color)` }} />
            </div>
        </div>
    );
}

const CommandBtn = ({ icon: Icon, onClick, color = "text-white", label }) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }}
        className={`group/btn relative p-3.5 backdrop-blur-2xl bg-black/60 border border-white/10 rounded-2xl transition-all hover:scale-110 active:scale-95 shadow-2xl ${color} hover:bg-black/80 hover:border-accent/40`}>
        <Icon size={18} />
        <span className="absolute right-full mr-4 top-1/2 -translate-y-1/2 px-4 py-2 bg-black/90 text-white text-[9px] font-black uppercase tracking-widest rounded-xl opacity-0 group-hover/btn:opacity-100 transition-all whitespace-nowrap border border-white/10 shadow-2xl pointer-events-none">{label}</span>
    </button>
);

const SpecMini = ({ icon: Icon, value }) => (
    <div className="flex items-center gap-2 p-3 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-main)] transition-all overflow-hidden">
        <Icon size={12} className="text-accent opacity-50 shrink-0" />
        <span className="text-[10px] font-black opacity-80 uppercase tracking-tighter text-auto truncate">{value}</span>
    </div>
);